import { useContext, useCallback, useState } from 'react';
import {
  TextToSpeechModifiersContext,
  TextToSpeechValuesContext,
  BookAppearanceModifiersContext,
  BookAppearanceValuesContext,
  BookLocationValuesContext,
  BookValuesContext,
} from 'contexts';
import { getLocalSettingsProxy } from 'utilities';

export const useTTSDialog = () => {
  const { isTTSDialogOpen, isPaused, didClickTTSDialogFit, readFrom } =
    useContext(TextToSpeechValuesContext);
  const {
    setIsTTSDialogOpen,
    setDidClickTTSDialogFit,
    setReadFrom,
    setTTSZoomPreference,
    read,
    readFromCfi,
  } = useContext(TextToSpeechModifiersContext);
  const { handleZoomFit } = useContext(BookAppearanceModifiersContext);
  const { isFitToScreen } = useContext(BookAppearanceValuesContext);
  const { currentBookLocation } = useContext(BookLocationValuesContext);
  const { book } = useContext(BookValuesContext);

  const [localSettings] = useState(() => getLocalSettingsProxy(book?.pk));

  const shouldShowTTSDialog =
    currentBookLocation?.document?.fixedWidth &&
    localSettings.shouldShowTTSDialog &&
    !isFitToScreen &&
    !isPaused;

  const handleTTSDialogClose = useCallback(() => {
    setIsTTSDialogOpen(false);
  }, [setIsTTSDialogOpen]);

  const handleTTSDialog = useCallback(
    (shouldSavePreferences, preference) => {
      if (shouldSavePreferences) {
        localSettings.shouldShowTTSDialog = false;
        setTTSZoomPreference(preference);
      }
      handleTTSDialogClose();
    },
    [localSettings, handleTTSDialogClose, setTTSZoomPreference],
  );

  const handleStartReading = useCallback(() => {
    if (readFrom) {
      readFromCfi(readFrom);
    } else {
      read();
    }
    setReadFrom(null);
  }, [read, readFromCfi, readFrom, setReadFrom]);

  const handleTTSDialogLeaveAsIs = useCallback(
    (shouldSavePreferences) => {
      handleTTSDialog(shouldSavePreferences, 'leaveAsIs');
      handleStartReading();
    },
    [handleTTSDialog, handleStartReading],
  );

  const handleTTSDialogFitToScreen = useCallback(
    (shouldSavePreferences) => {
      handleTTSDialog(shouldSavePreferences, 'fit');
      if (!isFitToScreen) {
        handleZoomFit(false);
      }
      setDidClickTTSDialogFit(true);
    },
    [handleTTSDialog, isFitToScreen, handleZoomFit, setDidClickTTSDialogFit],
  );

  const startTTSAfterFitToScreen = useCallback(() => {
    if (didClickTTSDialogFit) {
      handleStartReading();
      setDidClickTTSDialogFit(false);
    }
  }, [didClickTTSDialogFit, handleStartReading, setDidClickTTSDialogFit]);

  return {
    isTTSDialogOpen,
    setIsTTSDialogOpen,
    handleTTSDialogClose,
    handleTTSDialogLeaveAsIs,
    handleTTSDialogFitToScreen,
    shouldShowTTSDialog,
    startTTSAfterFitToScreen,
    setReadFrom,
    handleStartTTS: (startFn, readFromValue) => {
      if (localSettings.ttsZoomPreference === 'fit' && !isFitToScreen) {
        if (readFromValue) {
          setReadFrom(readFromValue);
        }
        handleZoomFit(false);
        setDidClickTTSDialogFit(true);
      } else {
        startFn();
      }
    },
  };
};
